import PropTypes from "prop-types";
import * as React from "react";
import { Dialog, Progress } from "../../themeV2";
import css from "./ComposerShell.module.css";
import revealFirstProblem from "./revealFirstProblem";

const ComposerShell = function ComposerShell({
  open,
  onRequestClose,
  onClosed,
  title,
  subtitle,
  steps,
  footer,
  revealToken = 0,
  appearInstantly = false,
  children,
}) {
  const bodyRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const stepIndex = steps?.current;
  const currentStep = steps?.items[steps.current];
  const isFirstRender = React.useRef(true);
  const [motion, setMotion] = React.useState({
    index: stepIndex,
    direction: undefined,
  });

  if (motion.index !== stepIndex) {
    setMotion({
      index: stepIndex,
      direction: stepIndex > motion.index ? "forward" : "back",
    });
  }

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    bodyRef.current?.scrollTo?.({ top: 0 });
    contentRef.current?.focus({ preventScroll: true });
  }, [stepIndex]);

  React.useEffect(() => {
    if (revealToken > 0) revealFirstProblem(contentRef.current);
  }, [revealToken]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => !next && onRequestClose()}
      onOpenChangeComplete={(isOpen) => !isOpen && onClosed()}
    >
      <Dialog.Popup
        size="full"
        className={css.popup}
        data-appear={appearInstantly ? "instant" : undefined}
      >
        <Dialog.AppBar
          end={subtitle && <Dialog.Description>{subtitle}</Dialog.Description>}
        >
          <Dialog.Title>{title}</Dialog.Title>
        </Dialog.AppBar>

        {steps && (
          <Progress
            variant="linear"
            value={((steps.current + 1) / steps.items.length) * 100}
            label={`Step ${steps.current + 1} of ${steps.items.length}`}
          />
        )}

        <Dialog.Body ref={bodyRef}>
          <div
            className={css.content}
            ref={contentRef}
            tabIndex={-1}
            aria-label={
              currentStep
                ? `${title}, ${currentStep.label}, step ${
                    steps.current + 1
                  } of ${steps.items.length}`
                : title
            }
          >
            <div
              key={stepIndex}
              className={css.step}
              data-direction={motion.direction}
            >
              {children}
            </div>
          </div>
        </Dialog.Body>

        {footer && <Dialog.Actions>{footer}</Dialog.Actions>}
      </Dialog.Popup>
    </Dialog.Root>
  );
};

ComposerShell.propTypes = {
  appearInstantly: PropTypes.bool,
  children: PropTypes.node,
  footer: PropTypes.node,
  onClosed: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  revealToken: PropTypes.number,
  steps: PropTypes.shape({
    current: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  subtitle: PropTypes.node,
  title: PropTypes.string.isRequired,
};

export default ComposerShell;
